'use client'

import { Check, ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { AUTO_SAVE_FEEDBACK_MS, useAutoSave } from '@/hooks/use-auto-save'
import { cn } from '@/lib/utils'
import { AutoSaveStatusIcon, autoSaveBorderClass } from './auto-save-status'
import { Button } from './button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu'

interface DropdownOption {
  value: string
  label: string
}

interface AutoSaveDropdownProps {
  /** Array of options to display in the dropdown */
  options: DropdownOption[]
  /** Current selected value */
  value: string | null
  /** Called immediately when the selected value changes (for optimistic UI updates) */
  onChange?: (value: string) => void
  /** Called when the selected value changes - should return a promise */
  onSave: (value: string) => Promise<void>
  /** Placeholder text when no value is selected */
  placeholder?: string
  /** Width of the dropdown */
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  /** Whether the dropdown is disabled */
  disabled?: boolean
  /** Additional class names for the trigger button */
  className?: string
  /** Duration in ms for the success/error indicator to show */
  feedbackDurationMs?: number
  /** Aria label for accessibility */
  'aria-label'?: string
}

const widthClasses = {
  sm: 'w-32',
  md: 'w-48',
  lg: 'w-64',
  xl: 'w-80',
  full: 'w-full',
}

/**
 * A dropdown component that auto-saves on selection with visual feedback.
 * Shows a green border animation on successful save, and a red border on error.
 * Displays a small icon indicating the current state:
 * - Blue spinning loader when saving
 * - Green check when saved successfully
 * - Red alert icon on error
 */
const AutoSaveDropdown = ({
  options,
  value,
  onChange,
  onSave,
  placeholder = 'Select an option',
  width = 'md',
  disabled = false,
  className,
  feedbackDurationMs = AUTO_SAVE_FEEDBACK_MS,
  'aria-label': ariaLabel,
}: AutoSaveDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [progress, setProgress] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  // Restart the border sweep whenever a save settles, either way.
  const startBorderSweep = useCallback(() => {
    setProgress(0)
    setIsAnimating(true)
  }, [])

  const { state: saveState, flush } = useAutoSave<string>({
    onSave,
    feedbackMs: feedbackDurationMs,
    onSaved: startBorderSweep,
    onError: startBorderSweep,
    logLabel: 'AutoSaveDropdown',
  })

  // Animate the border progress
  useEffect(() => {
    if (!isAnimating) return

    const animationDurationMs = feedbackDurationMs
    const steps = 60
    const increment = 100 / steps
    const intervalMs = animationDurationMs / steps

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer)
          setIsAnimating(false)
          return 0
        }
        return prev + increment
      })
    }, intervalMs)

    return () => clearInterval(timer)
  }, [isAnimating, feedbackDurationMs])

  const handleSelect = useCallback(
    (selectedValue: string) => {
      setIsOpen(false)

      // Call onChange immediately for optimistic UI updates
      if (onChange) {
        onChange(selectedValue)
      }

      // A selection is a deliberate discrete action — save it now, no debounce.
      flush(selectedValue)
    },
    [onChange, flush],
  )

  const selectedOption = options.find((opt) => opt.value === value)

  const getAnimatedBorderStyle = (): React.CSSProperties => {
    if (!isAnimating || saveState === 'idle' || saveState === 'saving') {
      return {}
    }

    const color = saveState === 'saved' ? 'rgb(34 197 94)' : 'rgb(239 68 68)'

    return {
      borderColor: 'transparent',
      background: `
        linear-gradient(to right, ${color} ${Math.min(progress * 4, 100)}%, transparent ${Math.min(progress * 4, 100)}%) top / 100% 2px no-repeat,
        linear-gradient(to bottom, ${color} ${Math.max(0, Math.min((progress - 25) * 4, 100))}%, transparent ${Math.max(0, Math.min((progress - 25) * 4, 100))}%) right / 2px 100% no-repeat,
        linear-gradient(to left, ${color} ${Math.max(0, Math.min((progress - 50) * 4, 100))}%, transparent ${Math.max(0, Math.min((progress - 50) * 4, 100))}%) bottom / 100% 2px no-repeat,
        linear-gradient(to top, ${color} ${Math.max(0, Math.min((progress - 75) * 4, 100))}%, transparent ${Math.max(0, Math.min((progress - 75) * 4, 100))}%) left / 2px 100% no-repeat
      `,
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setIsOpen(true)
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          aria-label={ariaLabel}
          disabled={disabled || saveState === 'saving'}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className={cn(
            'relative justify-between overflow-hidden transition-all duration-200',
            widthClasses[width],
            autoSaveBorderClass(saveState),
            className,
          )}
          style={getAnimatedBorderStyle()}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <div className="ml-2 flex shrink-0 items-center">
            <AutoSaveStatusIcon
              state={saveState}
              className="h-4 w-4"
              idle={<ChevronDown className="h-4 w-4 opacity-50" />}
            />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={cn(widthClasses[width])} align="start">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => handleSelect(option.value)}
            className="cursor-pointer"
          >
            <div className="flex w-full items-center justify-between">
              <span>{option.label}</span>
              {value === option.value && (
                <Check className="h-4 w-4 text-green-500" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { AutoSaveDropdown }
export type { AutoSaveDropdownProps, DropdownOption }
